/** Pressure — line weight follows stylus pressure (mouse falls back to speed). */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { Point } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { distance, strokeOutline } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19l7-7-4-4-7 7z"/><path d="M5 19h4"/><path d="M16 6l2-2"/></svg>';

export function createPressure(): CreateModule {
  let pts: Point[] = [];
  let pressures: number[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'pressure',
      kind: 'create',
      name: 'Pressure',
      apiVersion: 1,
      description: 'Line weight follows stylus pressure. Best with a pen; mouse uses speed.',
      icon: ICON,
      capabilities: ['pressure', 'tilt'],
      params: [
        { kind: 'slider', key: 'min', label: 'Min', min: 0.5, max: 20, step: 0.5, default: 1 },
        { kind: 'slider', key: 'max', label: 'Max', min: 2, max: 70, step: 1, default: 30 },
      ],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      pressures = [s.pressure];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 1.2) {
        pts.push(p);
        pressures.push(s.pressure);
      }
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const min = cfgNum(ctx.config, 'min', 1);
      const max = cfgNum(ctx.config, 'max', 30);
      const widths = pressures.map((p) => min + Math.max(0, Math.min(1, p)) * (max - min));
      const style = defaultStyle();
      style.mode = 'fill';
      style.color = { ...ctx.ink };
      const shape = ctx.newShape({ path: strokeOutline(pts, widths), style });
      shape.variableWidth = { spine: pts.map((p) => ({ ...p })), width: widths };
      return [shape];
    },

    onClear() {
      pts = [];
      pressures = [];
    },

    onCommit() {
      pts = [];
      pressures = [];
    },
  };
}
